import { z } from 'zod'
import type { Ejercicio } from '@domain/rules/mates.ts'
import type { AssistantConfig, AssistantFailure } from './assistant.ts'

/**
 * Cómo lo explicaría un maestro.
 *
 * Cuando un niño falla una cuenta, la app le dice cuánto era y sigue. Eso está
 * bien para no atascar la sesión, pero no enseña nada: saber que 34,5 + 12,7 son
 * 47,2 no es saber sumar con decimales. Esto pide la explicación del camino.
 *
 * Tres cosas que lo hacen aceptable en una app que por lo demás funciona en modo
 * avión:
 *
 * **Es opcional y no bloquea.** Si no hay red, o no hay servidor, o el modelo
 * tarda, el juego sigue exactamente igual: la explicación es un extra y su
 * ausencia no cambia nada.
 *
 * **El modelo no calcula.** La solución la ha resuelto el dominio, en enteros, y
 * se le manda ya hecha. Lo único que se le pide es el camino. Si pudiera dar el
 * resultado, antes o después le llevaría la contraria a la app delante de un
 * niño, y esa discusión el niño la pierde siempre.
 *
 * **Sale la cuenta y nada más.** Ni el nombre del niño, ni sus monedas, ni su
 * historial. La cuenta, la solución y el curso: con eso se explica, y es todo lo
 * que se va de la tablet.
 */

const ExplicacionSchema = z.object({
  pasos: z.array(z.string().trim().min(1).max(160)).min(1).max(4),
})

export type Explicacion = z.infer<typeof ExplicacionSchema>

export type ResultadoExplicacion =
  | { readonly ok: true; readonly explicacion: Explicacion }
  | { readonly ok: false; readonly failure: AssistantFailure }

const FALLO_POR_ESTADO: Readonly<Record<number, AssistantFailure>> = {
  400: 'respuesta-ilegible',
  401: 'rechazado',
  403: 'rechazado',
  502: 'sin-modelo',
  503: 'sin-modelo',
  504: 'tardo-demasiado',
}

/**
 * Doce segundos.
 *
 * Más corto que el del asistente —que es para un adulto sentado— porque al otro
 * lado hay un niño mirando la pantalla. Pasado ese rato, mejor decir que no se ha
 * podido y dejarle seguir que tenerlo esperando.
 */
const ESPERA_MS = 12_000

export async function pedirExplicacion(
  config: AssistantConfig,
  ejercicio: Ejercicio,
  curso: number,
  /**
   * Los pasos tal como los enseña la app, ya en texto. El modelo no inventa
   * el método: lo cuenta con otras palabras. Sin ellos, explica como pueda.
   */
  pasos: readonly string[] = [],
  signal?: AbortSignal,
): Promise<ResultadoExplicacion> {
  if (config.url.trim().length === 0) return { ok: false, failure: 'no-configurado' }

  const limite = new AbortController()
  const reloj = setTimeout(() => {
    limite.abort()
  }, ESPERA_MS)
  signal?.addEventListener('abort', () => {
    limite.abort()
  })

  try {
    const respuesta = await fetch(`${config.url.replace(/\/+$/, '')}/ia/explica`, {
      method: 'POST',
      signal: limite.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(config.token !== undefined && config.token.length > 0
          ? { Authorization: `Bearer ${config.token}` }
          : {}),
      },
      body: JSON.stringify({
        pide: ejercicio.pide,
        solucion: ejercicio.solucion,
        destreza: ejercicio.destreza,
        curso,
        pasos: pasos.slice(0, 24),
      }),
    })

    if (!respuesta.ok) {
      return { ok: false, failure: FALLO_POR_ESTADO[respuesta.status] ?? 'rechazado' }
    }

    const leido: unknown = await respuesta.json()
    const validado = ExplicacionSchema.safeParse(leido)
    // Lo que vuelve no se cree: si no tiene la forma acordada, no se enseña. Un
    // modelo que devuelve cualquier cosa no puede pintar nada en la pantalla de
    // un niño.
    if (!validado.success) return { ok: false, failure: 'respuesta-ilegible' }
    return { ok: true, explicacion: validado.data }
  } catch (causa) {
    const abortado = causa instanceof DOMException && causa.name === 'AbortError'
    return { ok: false, failure: abortado ? 'tardo-demasiado' : 'sin-conexion' }
  } finally {
    clearTimeout(reloj)
  }
}
